import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Button, TextField, Grid, Chip, Alert, Autocomplete,
  TableSortLabel, Card, CardContent, CardActions, IconButton, useTheme, useMediaQuery,
  Dialog, DialogContent, ToggleButton, ToggleButtonGroup, CircularProgress, Snackbar,
} from '@mui/material';
import {
  Add as AddIcon, Delete as DeleteIcon, AttachFile as AttachFileIcon,
  ContentCopy as ContentCopyIcon, InsertDriveFile as FileIcon,
  Close as CloseIcon, Search as SearchIcon,
} from '@mui/icons-material';
import InputAdornment from '@mui/material/InputAdornment';
import api from '../api';
import { useAuth } from '../AuthContext';
import { useColumnSettings } from '../hooks/useColumnSettings';
import { navyBlue } from '../constants/colors';
import { useThemeColors } from '../hooks/useThemeColors';

export default function PMSchedules() {
  const [pmSchedules, setPMSchedules] = useState([]);
  const [assets, setAssets] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [formData, setFormData] = useState({
    schedule_type: 'time_based',
    interval_unit: 'days',
    priority: 'medium',
    is_active: 1
  });
  const [error, setError] = useState('');
  const [orderBy, setOrderBy] = useState('next_due_date');
  const [order, setOrder] = useState('asc');
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [copyTargetAssets, setCopyTargetAssets] = useState([]);
  const [copying, setCopying] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  const [viewerFile, setViewerFile] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [checklistItems, setChecklistItems] = useState([]);
  const { hasRole } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const {
    priorityColors,
    borderColor,
    tableRowAlt,
    tableRowHover,
    statusColors,
    defaultTypeBackground,
    defaultTypeText,
    accentBlue,
    gradientPrimary,
    gradientPrimaryHover,
  } = useThemeColors();

  const defaultColumns = ['asset_number', 'asset_description', 'title', 'schedule_type', 'interval_value', 'next_due_date', 'priority', 'is_active'];
  const { visibleColumns, isColumnVisible } = useColumnSettings('pm_schedules', defaultColumns);

  const columnDefinitions = [
    { id: 'asset_number', label: 'Asset', sortKey: 'asset_number' },
    { id: 'asset_description', label: 'Asset Name', sortKey: 'asset_description' },
    { id: 'title', label: 'Title', sortKey: 'title' },
    { id: 'schedule_type', label: 'Type', sortKey: 'schedule_type' },
    { id: 'interval_value', label: 'Interval', sortKey: 'interval_value' },
    { id: 'next_due_date', label: 'Next Due', sortKey: 'next_due_date' },
    { id: 'priority', label: 'Priority', sortKey: 'priority' },
    { id: 'is_active', label: 'Status', sortKey: 'is_active' },
  ];

  // Toggle button styles
  const toggleButtonStyle = {
    borderRadius: '20px !important',
    border: `1.5px solid ${navyBlue} !important`,
    py: 0.5,
    px: 2,
    fontSize: '0.85rem',
    textTransform: 'none',
    '&.Mui-selected': {
      backgroundColor: navyBlue,
      color: 'white',
      '&:hover': {
        backgroundColor: '#2c4a6e',
      },
    },
    '&:hover': {
      backgroundColor: 'rgba(30, 58, 95, 0.08)',
    },
  };

  // Custom styled chip for priority
  const PriorityChip = ({ priority }) => {
    const colors = priorityColors[priority] || priorityColors.medium;
    return (
      <Chip
        label={formatChipLabel(priority)}
        size="small"
        sx={{
          backgroundColor: colors.bg,
          color: colors.text,
          border: `1px solid ${colors.border}`,
          fontWeight: 600,
        }}
      />
    );
  };

  const formatChipLabel = (text) => {
    if (!text) return 'N/A';
    if (text === 'time_based') return 'Time Based';
    if (text === 'meter_based') return 'Meter Based';
    if (text === 'usage_based') return 'Usage Based';
    return text.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [pmRes, assetsRes] = await Promise.all([
        api.get('/pm-schedules'),
        api.get('/assets'),
      ]);
      setPMSchedules(pmRes.data);
      setAssets(assetsRes.data);
    } catch (err) {
      setError('Failed to load PM schedules');
    }
  };

  const loadAttachments = async (pmId) => {
    try {
      const response = await api.get(`/uploads/pm-schedule/${pmId}`);
      setAttachments(response.data);
    } catch (err) {
      console.error('Failed to load attachments:', err);
    }
  };

  const loadChecklist = async (pmId) => {
    try {
      const response = await api.get(`/pm-schedules/${pmId}/checklist`);
      setChecklistItems(response.data.map(item => item.description));
    } catch (err) {
      console.error('Failed to load checklist:', err);
    }
  };

  const handleEdit = (pm) => {
    setFormData({
      id: pm.id,
      asset_id: pm.asset_id,
      title: pm.title,
      description: pm.description,
      schedule_type: pm.schedule_type,
      interval_value: pm.interval_value,
      interval_unit: pm.interval_unit,
      priority: pm.priority,
      estimated_labor_hours: pm.estimated_labor_hours,
      instructions: pm.instructions,
      is_active: pm.is_active,
      start_date: pm.next_due_date || '',
      start_meter_reading: pm.next_due_meter_reading || '',
      assignment_type: pm.assignment_type || 'technician'
    });
    setOpenDialog(true);
    loadAttachments(pm.id);
    loadChecklist(pm.id);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this PM schedule?')) {
      return;
    }
    try {
      await api.delete(`/pm-schedules/${id}`);
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete PM schedule');
    }
  };

  const handleSubmit = async () => {
    if (!formData.asset_id || !formData.title || !formData.schedule_type || !formData.interval_value || !formData.interval_unit) {
      setError('Asset, Title, Schedule Type, Interval, and Unit are required');
      return;
    }

    try {
      if (formData.id) {
        await api.put(`/pm-schedules/${formData.id}`, formData);
        // Save checklist items
        await api.put(`/pm-schedules/${formData.id}/checklist`, {
          items: checklistItems.filter(d => d.trim()).map((description, i) => ({ description, sort_order: i }))
        });
        setOpenDialog(false);
        setFormData({ schedule_type: 'time_based', interval_unit: 'days', priority: 'medium', is_active: 1 });
        setAttachments([]);
      } else {
        const response = await api.post('/pm-schedules', formData);
        const newId = response.data.id;
        // Save checklist items for new PM
        if (checklistItems.some(d => d.trim())) {
          await api.put(`/pm-schedules/${newId}/checklist`, {
            items: checklistItems.filter(d => d.trim()).map((description, i) => ({ description, sort_order: i }))
          });
        }
        // After creating, switch to edit mode so user can add attachments
        setFormData({ ...formData, id: newId });
        loadAttachments(newId);
        loadChecklist(newId);
        setSnackbar({ open: true, message: 'PM schedule created - you can now add attachments' });
      }
      setError('');
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || `Failed to ${formData.id ? 'update' : 'create'} PM schedule`);
    }
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const filteredPMSchedules = pmSchedules.filter((pm) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      (pm.title || '').toLowerCase().includes(term) ||
      (pm.asset_description || '').toLowerCase().includes(term)
    );
  });

  const sortedPMSchedules = [...filteredPMSchedules].sort((a, b) => {
    let aVal = a[orderBy];
    let bVal = b[orderBy];
    if (aVal === null || aVal === undefined) aVal = '';
    if (bVal === null || bVal === undefined) bVal = '';
    if (typeof aVal === 'string') {
      aVal = aVal.toLowerCase();
      bVal = bVal.toLowerCase();
    }
    if (order === 'asc') {
      return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
    } else {
      return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
    }
  });

  const handleCheckDue = async () => {
    try {
      const response = await api.post('/pm-schedules/check-due');
      const { message, workOrders } = response.data;
      if (workOrders.length > 0) {
        alert(`${message}\n\nWork Orders Created:\n${workOrders.map(wo => `- ${wo.work_order_number}: ${wo.pm_title}`).join('\n')}`);
      } else {
        alert('No due PMs found. All PMs are either not due yet or already have open work orders.');
      }
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to check due PMs');
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file || !formData.id) return;

    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post(`/uploads/pm-schedule/${formData.id}`, fd);
      loadAttachments(formData.id);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to upload file');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    if (!window.confirm('Delete this attachment?')) return;
    try {
      await api.delete(`/uploads/pm-attachment/${attachmentId}`);
      loadAttachments(formData.id);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete attachment');
    }
  };

  const handleCopyPM = async () => {
    if (copyTargetAssets.length === 0) return;
    setCopying(true);
    try {
      const response = await api.post(`/pm-schedules/${formData.id}/copy`, {
        asset_ids: copyTargetAssets.map(a => a.id)
      });
      setCopyDialogOpen(false);
      setCopyTargetAssets([]);
      setOpenDialog(false);
      setFormData({ schedule_type: 'time_based', interval_unit: 'days', priority: 'medium', is_active: 1 });
      setAttachments([]);
      setChecklistItems([]);
      loadData();
      setSnackbar({ open: true, message: response.data.message });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to copy PM schedule');
    } finally {
      setCopying(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const PMScheduleCard = ({ pm }) => (
    <Card
      onClick={() => handleEdit(pm)}
      sx={{
        mb: 2,
        borderRadius: 2,
        border: `1px solid ${borderColor}`,
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          boxShadow: 6,
          transform: 'translateY(-2px)',
          borderColor: navyBlue,
        },
        '&:active': {
          transform: 'scale(0.98)',
        }
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Typography
            variant="h6"
            component="div"
            sx={{ color: accentBlue, fontWeight: 600 }}
          >
            {pm.title}
          </Typography>
          <Chip
            label={pm.is_active ? 'Active' : 'Inactive'}
            size="small"
            sx={{
              backgroundColor: pm.is_active ? statusColors.completed.bg : defaultTypeBackground,
              color: pm.is_active ? statusColors.completed.text : defaultTypeText,
              border: `1px solid ${pm.is_active ? '#4caf50' : '#ddd'}`,
              fontWeight: 600,
            }}
          />
        </Box>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          <strong>Asset:</strong> {pm.asset_number} - {pm.asset_description}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          <strong>Type:</strong> {formatChipLabel(pm.schedule_type)}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          <strong>Interval:</strong> {pm.interval_value} {pm.interval_unit}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          <strong>Next Due:</strong> {pm.next_due_date || pm.next_due_meter_reading || 'N/A'}
        </Typography>
        <Box sx={{ mt: 1 }}>
          <PriorityChip priority={pm.priority} />
        </Box>
      </CardContent>
      {hasRole('administrator', 'supervisor') && (
        <CardActions sx={{ justifyContent: 'flex-end', pt: 0 }}>
          <IconButton
            size="small"
            color="error"
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(pm.id);
            }}
          >
            <DeleteIcon />
          </IconButton>
        </CardActions>
      )}
    </Card>
  );

  return (
    <Box>
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 3,
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ color: accentBlue, fontWeight: 600 }}>
          PM Schedules
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {hasRole('administrator', 'supervisor') && (
            <>
              <Button
                variant="outlined"
                onClick={handleCheckDue}
                size={isMobile ? 'small' : 'medium'}
                sx={{
                  whiteSpace: 'nowrap',
                  borderColor: navyBlue,
                  color: accentBlue,
                  borderRadius: 2,
                  textTransform: 'none',
                  '&:hover': {
                    borderColor: navyBlue,
                    backgroundColor: 'rgba(30, 58, 95, 0.08)',
                  },
                }}
              >
                Check Due PMs
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => { setChecklistItems([]); setOpenDialog(true); }}
                sx={{
                  background: gradientPrimary,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': {
                    background: gradientPrimaryHover,
                  },
                }}
              >
                {isMobile ? 'New' : 'New PM Schedule'}
              </Button>
            </>
          )}
        </Box>
      </Box>

      <TextField
        placeholder="Search by PM title or asset name..."
        size="small"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{ mb: 2, maxWidth: 400 }}
        fullWidth
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: 'text.secondary' }} />
            </InputAdornment>
          ),
        }}
      />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {isMobile ? (
        <Box>
          {sortedPMSchedules.map((pm) => (
            <PMScheduleCard key={pm.id} pm={pm} />
          ))}
        </Box>
      ) : (
        <Paper sx={{ borderRadius: 2, overflow: 'hidden', border: `1px solid ${borderColor}` }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: navyBlue }}>
                  {visibleColumns.map((columnId) => {
                    const column = columnDefinitions.find(col => col.id === columnId);
                    if (!column) return null;
                    return (
                      <TableCell key={columnId} sx={{ color: 'white', fontWeight: 600 }}>
                        <TableSortLabel
                          active={orderBy === column.sortKey}
                          direction={orderBy === column.sortKey ? order : 'asc'}
                          onClick={() => handleRequestSort(column.sortKey)}
                          sx={{
                            color: 'white !important',
                            '& .MuiTableSortLabel-icon': { color: 'white !important' },
                          }}
                        >
                          {column.label}
                        </TableSortLabel>
                      </TableCell>
                    );
                  })}
                  {hasRole('administrator', 'supervisor') && (
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>Actions</TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedPMSchedules.map((pm, index) => (
                  <TableRow
                    key={pm.id}
                    hover
                    onClick={() => handleEdit(pm)}
                    sx={{
                      backgroundColor: index % 2 === 0 ? tableRowAlt : 'inherit',
                      '&:hover': { backgroundColor: tableRowHover },
                      cursor: 'pointer',
                    }}
                  >
                    {visibleColumns.map((columnId) => {
                      if (!isColumnVisible(columnId)) return null;

                      switch (columnId) {
                        case 'asset_number':
                          return <TableCell key={columnId}>{pm.asset_number}</TableCell>;
                        case 'asset_description':
                          return <TableCell key={columnId}>{pm.asset_description}</TableCell>;
                        case 'title':
                          return (
                            <TableCell key={columnId}>
                              <Typography sx={{ color: accentBlue, fontWeight: 500 }}>
                                {pm.title}
                              </Typography>
                            </TableCell>
                          );
                        case 'schedule_type':
                          return <TableCell key={columnId}>{formatChipLabel(pm.schedule_type)}</TableCell>;
                        case 'interval_value':
                          return <TableCell key={columnId}>{pm.interval_value} {pm.interval_unit}</TableCell>;
                        case 'next_due_date':
                          return <TableCell key={columnId}>{pm.next_due_date || pm.next_due_meter_reading || 'N/A'}</TableCell>;
                        case 'priority':
                          return (
                            <TableCell key={columnId}>
                              <PriorityChip priority={pm.priority} />
                            </TableCell>
                          );
                        case 'is_active':
                          return (
                            <TableCell key={columnId}>
                              <Chip
                                label={pm.is_active ? 'Active' : 'Inactive'}
                                size="small"
                                sx={{
                                  backgroundColor: pm.is_active ? statusColors.completed.bg : defaultTypeBackground,
                                  color: pm.is_active ? statusColors.completed.text : defaultTypeText,
                                  border: `1px solid ${pm.is_active ? '#4caf50' : '#ddd'}`,
                                  fontWeight: 600,
                                }}
                              />
                            </TableCell>
                          );
                        default:
                          return null;
                      }
                    })}
                    {hasRole('administrator', 'supervisor') && (
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <IconButton size="small" color="error" onClick={() => handleDelete(pm.id)}>
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* PM Schedule Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => {
          setOpenDialog(false);
          setFormData({ schedule_type: 'time_based', interval_unit: 'days', priority: 'medium', is_active: 1 });
          setAttachments([]);
          setChecklistItems([]);
          setError('');
        }}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <Box sx={{ background: gradientPrimary, py: 2, px: 3 }}>
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
            {formData.id ? 'Edit PM Schedule' : 'New PM Schedule'}
          </Typography>
        </Box>
        <DialogContent sx={{ pt: 3 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Autocomplete
                options={assets}
                getOptionLabel={(asset) => `${asset.asset_number} - ${asset.asset_description}`}
                value={assets.find(a => a.id === formData.asset_id) || null}
                onChange={(event, newValue) => {
                  const isFacilities = newValue?.category === 'Facilities';
                  setFormData({
                    ...formData,
                    asset_id: newValue ? newValue.id : '',
                    assignment_type: isFacilities ? (formData.assignment_type || 'technician') : 'technician'
                  });
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Asset *" fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                )}
              />
            </Grid>
            {assets.find(a => a.id === formData.asset_id)?.category === 'Facilities' && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: accentBlue, fontWeight: 600 }}>
                  Assign To
                </Typography>
                <ToggleButtonGroup
                  value={formData.assignment_type || 'technician'}
                  exclusive
                  onChange={(e, val) => val && setFormData({ ...formData, assignment_type: val })}
                  sx={{ gap: 1 }}
                >
                  <ToggleButton value="technician" sx={toggleButtonStyle}>Technician</ToggleButton>
                  <ToggleButton value="janitor" sx={toggleButtonStyle}>Janitor</ToggleButton>
                </ToggleButtonGroup>
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Title *"
                value={formData.title || ''}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={2}
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: accentBlue, fontWeight: 600 }}>
                Schedule Type *
              </Typography>
              <ToggleButtonGroup
                value={formData.schedule_type}
                exclusive
                onChange={(e, val) => {
                  if (val) {
                    setFormData({
                      ...formData,
                      schedule_type: val,
                      interval_unit: val === 'time_based' ? 'days' : 'hours'
                    });
                  }
                }}
                sx={{ gap: 1 }}
              >
                <ToggleButton value="time_based" sx={toggleButtonStyle}>Time Based</ToggleButton>
                <ToggleButton value="usage_based" sx={toggleButtonStyle}>Usage Based</ToggleButton>
              </ToggleButtonGroup>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: accentBlue, fontWeight: 600 }}>
                Priority
              </Typography>
              <ToggleButtonGroup
                value={formData.priority || 'medium'}
                exclusive
                onChange={(e, val) => val && setFormData({ ...formData, priority: val })}
                sx={{ flexWrap: 'wrap', gap: 1 }}
              >
                <ToggleButton value="low" sx={{ ...toggleButtonStyle, '&.Mui-selected': { backgroundColor: '#4caf50', color: 'white', borderColor: '#4caf50 !important' } }}>
                  Low
                </ToggleButton>
                <ToggleButton value="medium" sx={{ ...toggleButtonStyle, '&.Mui-selected': { backgroundColor: '#2196f3', color: 'white', borderColor: '#2196f3 !important' } }}>
                  Medium
                </ToggleButton>
                <ToggleButton value="high" sx={{ ...toggleButtonStyle, '&.Mui-selected': { backgroundColor: '#ff9800', color: 'white', borderColor: '#ff9800 !important' } }}>
                  High
                </ToggleButton>
                <ToggleButton value="emergency" sx={{ ...toggleButtonStyle, '&.Mui-selected': { backgroundColor: '#f44336', color: 'white', borderColor: '#f44336 !important' } }}>
                  Emergency
                </ToggleButton>
              </ToggleButtonGroup>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Interval *"
                type="number"
                value={formData.interval_value || ''}
                onChange={(e) => setFormData({ ...formData, interval_value: parseInt(e.target.value) || '' })}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: accentBlue, fontWeight: 600 }}>
                Unit *
              </Typography>
              <ToggleButtonGroup
                value={formData.interval_unit}
                exclusive
                onChange={(e, val) => val && setFormData({ ...formData, interval_unit: val })}
                sx={{ flexWrap: 'wrap', gap: 1 }}
              >
                {formData.schedule_type === 'time_based' ? (
                  <>
                    <ToggleButton value="days" sx={toggleButtonStyle}>Days</ToggleButton>
                    <ToggleButton value="weeks" sx={toggleButtonStyle}>Weeks</ToggleButton>
                    <ToggleButton value="months" sx={toggleButtonStyle}>Months</ToggleButton>
                    <ToggleButton value="years" sx={toggleButtonStyle}>Years</ToggleButton>
                  </>
                ) : (
                  <>
                    <ToggleButton value="hours" sx={toggleButtonStyle}>Hours</ToggleButton>
                    <ToggleButton value="cycles" sx={toggleButtonStyle}>Cycles</ToggleButton>
                  </>
                )}
              </ToggleButtonGroup>
            </Grid>
            {formData.schedule_type === 'time_based' && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Start Date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={formData.start_date || ''}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  helperText="First scheduled maintenance date"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
            )}
            {formData.schedule_type === 'usage_based' && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Start Meter Reading"
                  type="number"
                  value={formData.start_meter_reading || ''}
                  onChange={(e) => setFormData({ ...formData, start_meter_reading: parseInt(e.target.value) || '' })}
                  helperText="Current or starting meter reading"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Estimated Labor Hours"
                type="number"
                inputProps={{ step: 0.5 }}
                value={formData.estimated_labor_hours || ''}
                onChange={(e) => setFormData({ ...formData, estimated_labor_hours: parseFloat(e.target.value) || '' })}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Instructions"
                multiline
                rows={3}
                value={formData.instructions || ''}
                onChange={(e) => setFormData({ ...formData, instructions: e.target.value })}
                helperText="Detailed maintenance instructions"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            {/* Checklist section */}
            {hasRole('administrator', 'supervisor') && (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ color: accentBlue, fontWeight: 600 }}>
                    Checklist Items
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => setChecklistItems([...checklistItems, ''])}
                    sx={{ textTransform: 'none', color: accentBlue }}
                    startIcon={<AddIcon />}
                  >
                    Add Item
                  </Button>
                </Box>
                {checklistItems.length === 0 ? (
                  <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                    No checklist items
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {checklistItems.map((item, index) => (
                      <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" color="textSecondary" sx={{ minWidth: 20 }}>
                          {index + 1}.
                        </Typography>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="Checklist item description"
                          value={item}
                          onChange={(e) => {
                            const updated = [...checklistItems];
                            updated[index] = e.target.value;
                            setChecklistItems(updated);
                          }}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setChecklistItems(checklistItems.filter((_, i) => i !== index))}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                )}
              </Grid>
            )}
            {/* Attachments section - only when editing */}
            {formData.id && (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ color: accentBlue, fontWeight: 600 }}>
                    Attachments
                  </Typography>
                  {hasRole('administrator', 'supervisor') && (
                    <Button
                      component="label"
                      size="small"
                      startIcon={uploading ? <CircularProgress size={16} /> : <AttachFileIcon />}
                      disabled={uploading}
                      sx={{ textTransform: 'none', color: accentBlue }}
                    >
                      {uploading ? 'Uploading...' : 'Add File'}
                      <input type="file" hidden onChange={handleFileUpload} />
                    </Button>
                  )}
                </Box>
                {attachments.length === 0 ? (
                  <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                    No attachments
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {attachments.map((att) => (
                      <Box
                        key={att.id}
                        onClick={() => setViewerFile(att)}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          p: 1,
                          borderRadius: 1,
                          border: `1px solid ${borderColor}`,
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: 'rgba(30, 58, 95, 0.04)' },
                        }}
                      >
                        <FileIcon sx={{ color: accentBlue, fontSize: 20 }} />
                        <Typography
                          sx={{
                            flex: 1,
                            color: accentBlue,
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {att.original_filename}
                        </Typography>
                        <Typography variant="caption" color="textSecondary" sx={{ whiteSpace: 'nowrap' }}>
                          {formatFileSize(att.file_size)}
                        </Typography>
                        {hasRole('administrator', 'supervisor') && (
                          <IconButton
                            size="small"
                            color="error"
                            onClick={(e) => { e.stopPropagation(); handleDeleteAttachment(att.id); }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    ))}
                  </Box>
                )}
              </Grid>
            )}
            {/* Desktop buttons inside grid */}
            {!isMobile && (
              <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
                <Button
                  onClick={() => {
                    setOpenDialog(false);
                    setFormData({ schedule_type: 'time_based', interval_unit: 'days', priority: 'medium', is_active: 1 });
                    setAttachments([]);
                    setChecklistItems([]);
                    setError('');
                  }}
                  size="medium"
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    px: 3,
                    py: 1,
                    fontSize: '0.875rem',
                  }}
                >
                  Cancel
                </Button>
                {formData.id && hasRole('administrator', 'supervisor') && (
                  <Button
                    onClick={() => setCopyDialogOpen(true)}
                    variant="outlined"
                    size="medium"
                    startIcon={<ContentCopyIcon />}
                    sx={{
                      borderRadius: 2,
                      textTransform: 'none',
                      px: 3,
                      py: 1,
                      fontSize: '0.875rem',
                      borderColor: navyBlue,
                      color: accentBlue,
                    }}
                  >
                    Copy to Assets
                  </Button>
                )}
                <Button
                  onClick={handleSubmit}
                  variant="contained"
                  size="medium"
                  sx={{
                    background: gradientPrimary,
                    borderRadius: 2,
                    textTransform: 'none',
                    px: 3,
                    py: 1,
                    fontSize: '0.875rem',
                  }}
                >
                  {formData.id ? 'Update' : 'Create'}
                </Button>
              </Grid>
            )}
            {/* Spacer for mobile sticky buttons */}
            {isMobile && <Grid item xs={12} sx={{ height: 80 }} />}
          </Grid>
        </DialogContent>
        {/* Mobile sticky buttons */}
        {isMobile && (
          <Box
            sx={{
              position: 'sticky',
              bottom: 0,
              left: 0,
              right: 0,
              bgcolor: 'background.paper',
              borderTop: `1px solid ${borderColor}`,
              p: 2,
              display: 'flex',
              justifyContent: 'center',
              gap: 2,
              zIndex: 1,
              flexWrap: 'wrap',
            }}
          >
            <Button
              onClick={() => {
                setOpenDialog(false);
                setFormData({ schedule_type: 'time_based', interval_unit: 'days', priority: 'medium', is_active: 1 });
                setAttachments([]);
                setChecklistItems([]);
                setError('');
              }}
              size="large"
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                px: 4,
                py: 1.5,
                fontSize: '1rem',
                flex: 1,
                maxWidth: 150,
              }}
            >
              Cancel
            </Button>
            {formData.id && hasRole('administrator', 'supervisor') && (
              <Button
                onClick={() => setCopyDialogOpen(true)}
                variant="outlined"
                size="large"
                startIcon={<ContentCopyIcon />}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  px: 3,
                  py: 1.5,
                  fontSize: '1rem',
                  borderColor: navyBlue,
                  color: accentBlue,
                  flex: 1,
                  maxWidth: 200,
                }}
              >
                Copy
              </Button>
            )}
            <Button
              onClick={handleSubmit}
              variant="contained"
              size="large"
              sx={{
                background: gradientPrimary,
                borderRadius: 2,
                textTransform: 'none',
                px: 4,
                py: 1.5,
                fontSize: '1rem',
                flex: 1,
                maxWidth: 200,
              }}
            >
              {formData.id ? 'Update' : 'Create'}
            </Button>
          </Box>
        )}
      </Dialog>

      {/* Copy PM Dialog */}
      <Dialog
        open={copyDialogOpen}
        onClose={() => { setCopyDialogOpen(false); setCopyTargetAssets([]); }}
        maxWidth="sm"
        fullWidth
      >
        <Box sx={{ background: gradientPrimary, py: 2, px: 3 }}>
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
            Copy PM to Assets
          </Typography>
        </Box>
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Copy <strong>{formData.title}</strong> ({formData.interval_value} {formData.interval_unit}) to the selected assets.
            {attachments.length > 0 && ` ${attachments.length} attachment(s) will also be copied.`}
          </Typography>
          <Autocomplete
            multiple
            options={assets.filter(a => a.id !== formData.asset_id)}
            getOptionLabel={(asset) => `${asset.asset_number} - ${asset.asset_description}`}
            value={copyTargetAssets}
            onChange={(event, newValue) => setCopyTargetAssets(newValue)}
            renderInput={(params) => (
              <TextField {...params} label="Target Assets" placeholder="Select assets..." sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            )}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
            <Button
              onClick={() => { setCopyDialogOpen(false); setCopyTargetAssets([]); }}
              sx={{ borderRadius: 2, textTransform: 'none' }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCopyPM}
              variant="contained"
              disabled={copyTargetAssets.length === 0 || copying}
              startIcon={copying ? <CircularProgress size={16} /> : <ContentCopyIcon />}
              sx={{
                background: gradientPrimary,
                borderRadius: 2,
                textTransform: 'none',
              }}
            >
              {copying ? 'Copying...' : `Copy to ${copyTargetAssets.length} Asset${copyTargetAssets.length !== 1 ? 's' : ''}`}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* File Viewer Dialog */}
      <Dialog
        open={Boolean(viewerFile)}
        onClose={() => setViewerFile(null)}
        fullScreen
      >
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: gradientPrimary,
          py: 1.5,
          px: 2,
        }}>
          <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, mr: 1 }}>
            {viewerFile?.original_filename}
          </Typography>
          <IconButton onClick={() => setViewerFile(null)} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', bgcolor: '#f5f5f5', overflow: 'auto' }}>
          {viewerFile && (
            viewerFile.file_type?.startsWith('image/') ? (
              <img
                src={`/uploads/${viewerFile.filename}`}
                alt={viewerFile.original_filename}
                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
              />
            ) : (
              <iframe
                src={`/uploads/${viewerFile.filename}`}
                title={viewerFile.original_filename}
                style={{ width: '100%', height: '100%', border: 'none' }}
              />
            )
          )}
        </Box>
      </Dialog>

      {/* Success snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ open: false, message: '' })}
        message={snackbar.message}
      />
    </Box>
  );
}
